/**
 * Auth API – calls backend auth endpoints.
 * Uses VITE_API_BASE_URL; all responses follow ApiResponse<T>.
 */

import type {
  User,
  ApiResponse,
  RegisterRequest,
  LoginRequest,
  LoginResponse,
  LoginRequiresVerificationResponse,
  LoginRequires2FAResponse,
  VerifyEmailRequest,
  VerifyPhoneRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ConfirmEmailChangeResponse,
  CompleteInvitationRequest,
  CompleteInvitationResponse,
  Enable2FARequest,
  Enable2FAResponse,
  Verify2FASetupRequest,
  Verify2FASetupResponse,
  Send2FACodeResponse,
} from '@lambda/shared/types';
import { handleApiUnauthorized } from '@utils/apiUnauthorized';
import {
  SessionRefreshTransientError,
  SessionRefreshUnauthorizedError,
} from '@utils/sessionRestore';
import { apiFetchUrl, assertApiConfigured } from '@constants/apiConfig';
import { apiRequest, type ApiRequestOptions } from './apiClient';

/** Auth endpoints are public: no bearer token and no global 401 handling. */
function request<T>(path: string, options: ApiRequestOptions) {
  return apiRequest<T>(path, null, options);
}

/** Coalesce concurrent refresh network calls (Strict Mode remount + restore retries). */
let inflightRefresh: Promise<ApiResponse<LoginResponse>> | null = null;

async function fetchRefreshSession(): Promise<ApiResponse<LoginResponse>> {
  assertApiConfigured();
  let res: Response;
  try {
    res = await fetch(apiFetchUrl('/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
  } catch {
    throw new SessionRefreshTransientError('Could not reach the server. Check your connection.');
  }

  const data = (await res.json().catch(() => ({}))) as ApiResponse<LoginResponse>;

  if (res.status === 401) {
    throw new SessionRefreshUnauthorizedError(
      data?.error?.message ?? 'Session expired. Please log in again.',
      data?.error?.code
    );
  }

  if (!res.ok) {
    throw new SessionRefreshTransientError(
      data?.error?.message ?? `Session refresh failed (${res.status}).`
    );
  }

  return data;
}

export const authApi = {
  register(data: RegisterRequest) {
    return request<{
      user?: User;
      message: string;
      email?: string;
      emailCode?: string;
      phoneCode?: string;
      /** Present only when a new account was created (not silent anti-enumeration 201s). */
      isNewRegistration?: boolean;
    }>('/auth/register', { method: 'POST', payload: data });
  },

  /** Returns full LoginResponse, or LoginRequiresVerificationResponse (redirect to verify-email/verify-phone), or LoginRequires2FAResponse when 2FA is required. */
  login(data: LoginRequest) {
    return request<
      LoginResponse | LoginRequiresVerificationResponse | LoginRequires2FAResponse
    >('/auth/login', {
      method: 'POST',
      payload: data,
      credentials: 'include',
    });
  },

  enable2FA(accessToken: string, body?: Enable2FARequest) {
    return request<Enable2FAResponse>('/auth/enable-2fa', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      payload: body ?? {},
    });
  },

  /** For SMS 2FA: request a code to be sent to the user's phone (local dev may return smsCode). */
  send2FACode(tempToken: string) {
    return request<Send2FACodeResponse>('/auth/send-2fa-code', {
      method: 'POST',
      payload: { tempToken },
    });
  },

  verify2FASetup(accessToken: string, data: Verify2FASetupRequest) {
    return request<Verify2FASetupResponse>('/auth/verify-2fa-setup', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      payload: data,
    });
  },

  verify2FALogin(tempToken: string, code: string, rememberDevice = false) {
    return request<LoginResponse>('/auth/verify-2fa-login', {
      method: 'POST',
      payload: { tempToken, code, rememberDevice },
      credentials: 'include',
    });
  },

  /**
   * Exchange the HttpOnly refresh-token cookie for a new access token.
   * Concurrent callers share one in-flight request so we do not race cookie rotation.
   */
  async refresh(options?: { sessionRestore?: boolean }) {
    if (!inflightRefresh) {
      inflightRefresh = fetchRefreshSession().finally(() => {
        inflightRefresh = null;
      });
    }
    try {
      return await inflightRefresh;
    } catch (err) {
      if (err instanceof SessionRefreshUnauthorizedError && !options?.sessionRestore) {
        handleApiUnauthorized(401, {
          error: { code: err.code ?? 'INVALID_TOKEN', message: err.message },
        });
      }
      throw err;
    }
  },

  verifyEmail(data: VerifyEmailRequest) {
    return request<{ user: User; message: string }>('/auth/verify-email', {
      method: 'POST',
      payload: data,
    });
  },

  verifyPhone(data: VerifyPhoneRequest) {
    return request<{
      user: User;
      message: string;
      accessToken?: string;
      refreshToken?: string;
      expiresIn?: number;
    }>('/auth/verify-phone', {
      method: 'POST',
      payload: data,
      credentials: 'include',
    });
  },

  resendEmailCode(email: string) {
    return request<{ message: string; emailCode?: string }>('/auth/resend-email-code', {
      method: 'POST',
      payload: { email },
    });
  },

  resendPhoneCode(data: { email?: string; userId?: string }) {
    return request<{ message: string; phoneCode?: string }>('/auth/resend-phone-code', {
      method: 'POST',
      payload: data,
    });
  },

  forgotPassword(data: ForgotPasswordRequest) {
    return request<{ message: string; resetToken?: string }>('/auth/forgot-password', {
      method: 'POST',
      payload: data,
    });
  },

  resetPassword(data: ResetPasswordRequest) {
    return request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      payload: data,
    });
  },

  confirmEmailChange(data: { token: string }) {
    return request<ConfirmEmailChangeResponse>('/auth/confirm-email-change', {
      method: 'POST',
      payload: data,
    });
  },

  /** Get invitation details by token for pre-filling complete-invitation form (no auth). */
  getInvitation(token: string) {
    const encoded = encodeURIComponent(token);
    return request<{ email: string; name?: string; role: string }>(
      `/auth/invitation?token=${encoded}`,
      { method: 'GET' }
    );
  },

  /** Complete invitation with token; creates account (pending-verification). Returns user + message; no tokens. User goes through verify-email → verify-phone → setup-2FA like self-register. */
  completeInvitation(data: CompleteInvitationRequest) {
    return request<CompleteInvitationResponse>('/auth/complete-invitation', {
      method: 'POST',
      payload: data,
    });
  },

  /** Log out. Sends cookie so server clears it; still clears local state if call fails (e.g. offline). */
  logout(accessToken: string | null) {
    return request<{ message: string }>('/auth/logout', {
      method: 'POST',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      credentials: 'include',
    });
  },
};
